import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClockIcon,
  DocumentTextIcon,
  EyeIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  SwatchIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { saveAs } from 'file-saver';
import { useTaskSubscription } from '../hooks/useTaskSubscription';
import {
  ChapterReferenceSelection,
  chapterReferenceApi,
  ContentChapter,
  ContentDashboard,
  contentWorkspaceApi,
  getErrorMessage,
  PersistedTaskSnapshot,
  ReferenceBid,
  ReferenceBidPreview,
  taskApi,
  WordExportRecord,
  wordExportApi,
} from '../services/api';
import {
  ReferenceBidConfigModal,
  ReferenceFileSelection,
} from '../components/ReferenceBidConfigModal';
import { makeIdempotencyKey } from '../utils/idempotencyKey';

interface DurableContentWorkspaceProps {
  projectId?: number | null;
  projectName?: string;
}

type SingleOperation = 'regenerate' | 'expand' | 'rewrite';
type ViewMode = 'outline' | 'content';

interface OperationDialog {
  chapter: ContentChapter;
  type: SingleOperation;
}

const BLOCKING_TASK_STATUSES = new Set([0, 10, 20, 60, 80, 90]);

const operationLabels: Record<SingleOperation, string> = {
  regenerate: '重生成',
  expand: '扩写',
  rewrite: '改写',
};

const DEFAULT_STYLES = [
  { key: '通用', label: '通用', description: '采用标准招投标文档风格，语言规范、术语准确' },
  { key: '实用', label: '实用', description: '内容务实、直击要点，少空泛修饰，突出可落地措施' },
  { key: '一二级显示摘要', label: '一二级显示摘要', description: '一、二级章节在正文前添加简短摘要，其余层级不添加' },
  { key: '前三级显示摘要', label: '前三级显示摘要', description: '一至三级章节在正文前添加简短摘要，其余层级不添加' },
  { key: '每一级都是总分结构', label: '每一级都是总分结构', description: '每一级章节均采用"先总述后分述"的总分结构组织内容' },
];

const formatNumber = (value: number) =>
  new Intl.NumberFormat('zh-CN').format(value);

const buildChapterTree = (chapters: ContentChapter[]): ContentChapter[] => {
  const map = new Map<string, ContentChapter>();
  const roots: ContentChapter[] = [];

  chapters.forEach((ch) => {
    map.set(ch.outline_id, { ...ch, children: [] });
  });

  chapters.forEach((chapter) => {
    const parentId = chapter.parent_id;
    if (parentId && typeof parentId === 'string' && map.has(parentId)) {
      const parent = map.get(parentId)!;
      parent.children = parent.children || [];
      parent.children.push(chapter);
    } else {
      roots.push(chapter);
    }
  });

  const sortTree = (nodes: ContentChapter[]) => {
    nodes.sort((a, b) => a.sort_order - b.sort_order);
    nodes.forEach((node) => {
      if (node.children && node.children.length > 0) {
        sortTree(node.children);
      }
    });
  };

  sortTree(roots);
  return roots;
};

const getLeafChapters = (chapters: ContentChapter[]): ContentChapter[] => {
  const tree = buildChapterTree(chapters);
  const result: ContentChapter[] = [];

  const traverse = (nodes: ContentChapter[]) => {
    nodes.forEach((node) => {
      if (!node.children || node.children.length === 0) {
        result.push(node);
      } else {
        traverse(node.children);
      }
    });
  };

  traverse(tree);
  return result;
};

const findChapterInTree = (
  nodes: ContentChapter[],
  outlineId: string
): ContentChapter | null => {
  for (const node of nodes) {
    if (node.outline_id === outlineId) return node;
    if (node.children && node.children.length > 0) {
      const found = findChapterInTree(node.children, outlineId);
      if (found) return found;
    }
  }
  return null;
};

interface ChapterNodeProps {
  chapter: ContentChapter;
  level: number;
  expandedIds: Set<string>;
  onToggle: (id: string) => void;
  onSelect: (chapter: ContentChapter) => void;
  onToggleImageRich: (chapter: ContentChapter) => void;
  onStartWrite: (chapter: ContentChapter) => void;
  selectedId: string;
  canGenerate: boolean;
}

const ChapterNode: React.FC<ChapterNodeProps> = ({
  chapter,
  level,
  expandedIds,
  onToggle,
  onSelect,
  onToggleImageRich,
  onStartWrite,
  selectedId,
  canGenerate,
}) => {
  const hasChildren = chapter.children && chapter.children.length > 0;
  const isExpanded = expandedIds.has(chapter.outline_id);
  const isLeaf = !hasChildren;
  const isSelected = selectedId === chapter.outline_id;

  const statusIcon = chapter.has_content && !chapter.last_error_code ? (
    <CheckCircleIcon className="h-4 w-4 shrink-0 text-emerald-500" />
  ) : chapter.last_error_code ? (
    <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-red-500" />
  ) : (
    <ClockIcon className="h-4 w-4 shrink-0 text-slate-400" />
  );

  return (
    <div>
      <div
        className={`flex items-center gap-1 rounded-lg border px-2 py-1.5 transition cursor-pointer ${
          isSelected
            ? 'border-blue-200 bg-blue-50'
            : 'border-transparent hover:bg-slate-50'
        }`}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={() => onSelect(chapter)}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggle(chapter.outline_id);
            }}
            className="flex h-4 w-4 shrink-0 items-center justify-center text-slate-400 hover:text-slate-600"
          >
            {isExpanded ? (
              <ChevronDownIcon className="h-3 w-3" />
            ) : (
              <ChevronRightIcon className="h-3 w-3" />
            )}
          </button>
        ) : (
          <span className="w-4 shrink-0" />
        )}
        {statusIcon}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-slate-800">
            {chapter.title}
          </p>
          {isLeaf && (
            <p className="text-xs text-slate-400">
              {formatNumber(chapter.actual_word_count)} 字
              {chapter.expected_word_count > 0
                ? ` / ${formatNumber(chapter.expected_word_count)} 字`
                : ''}
            </p>
          )}
        </div>
        {isLeaf && (
          <div className="flex shrink-0 items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <span className="text-xs text-slate-400">图文并茂</span>
            <button
              type="button"
              onClick={() => onToggleImageRich(chapter)}
              className={`relative inline-flex h-4 w-7 items-center rounded-full transition ${
                chapter.is_image_rich ? 'bg-blue-500' : 'bg-slate-300'
              }`}
            >
              <span
                className={`inline-block h-3 w-3 transform rounded-full bg-white transition ${
                  chapter.is_image_rich ? 'translate-x-3.5' : 'translate-x-0.5'
                }`}
              />
            </button>
            {canGenerate && (
              <button
                type="button"
                onClick={() => onStartWrite(chapter)}
                className="rounded bg-blue-50 px-2 py-0.5 text-xs text-blue-600 hover:bg-blue-100"
              >
                {chapter.has_content ? '重写' : '开始编写'}
              </button>
            )}
          </div>
        )}
      </div>
      {hasChildren && isExpanded && (
        <div>
          {chapter.children!.map((child) => (
            <ChapterNode
              key={child.outline_id}
              chapter={child}
              level={level + 1}
              expandedIds={expandedIds}
              onToggle={onToggle}
              onSelect={onSelect}
              onToggleImageRich={onToggleImageRich}
              onStartWrite={onStartWrite}
              selectedId={selectedId}
              canGenerate={canGenerate}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/** 递归查找树中指定 outline_id 的章节 */
const findChapterById = (
  nodes: ContentChapter[],
  outlineId: string
): ContentChapter | null => {
  for (const node of nodes) {
    if (node.outline_id === outlineId) return node;
    if (node.children && node.children.length > 0) {
      const found = findChapterById(node.children, outlineId);
      if (found) return found;
    }
  }
  return null;
};

interface OutlineChapterRowProps {
  chapter: ContentChapter;
  depth: number;
  onPreview: (chapter: ContentChapter) => void;
  onStartWrite: (chapter: ContentChapter) => void;
  onOpenReference: (chapter: ContentChapter) => void;
  selectedReferences: Record<string, ReferenceFileSelection>;
  canGenerate: boolean;
  children?: React.ReactNode;
}

/** 目录模式下的章节行组件，按级别显示不同功能 */
const OutlineChapterRow: React.FC<OutlineChapterRowProps> = ({
  chapter,
  depth,
  onPreview,
  onStartWrite,
  onOpenReference,
  selectedReferences,
  canGenerate,
  children,
}) => {
  // depth >= 2 表示三级及以下节点，显示完整功能
  const isDetailLevel = depth >= 2;
  // 三级节点的子项作为预览文本显示
  const hasSubItems = isDetailLevel && chapter.children && chapter.children.length > 0;

  // 状态标记：仅三级及以下节点显示
  const statusDot = isDetailLevel ? (
    chapter.has_content && !chapter.last_error_code ? (
      <span className="flex items-center gap-1">
        <span className="h-2 w-2 rounded-full bg-emerald-500" />
        <span className="text-xs text-emerald-600">编写完成</span>
      </span>
    ) : chapter.last_error_code ? (
      <span className="flex items-center gap-1">
        <span className="h-2 w-2 rounded-full bg-red-500" />
        <span className="text-xs text-red-600">编写异常</span>
      </span>
    ) : chapter.generation_status === 20 || chapter.generation_status === 10 ? (
      <span className="flex items-center gap-1">
        <span className="h-2 w-2 rounded-full bg-amber-500" />
        <span className="text-xs text-amber-600">编写中</span>
      </span>
    ) : (
      <span className="flex items-center gap-1">
        <span className="h-2 w-2 rounded-full bg-slate-300" />
        <span className="text-xs text-slate-400">未编写</span>
      </span>
    )
  ) : null;

  // 参考技术标按钮：仅三级及以下节点显示
  const refButton = isDetailLevel ? (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onOpenReference(chapter);
      }}
      className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-white px-2 py-0.5 text-xs text-blue-600 hover:bg-blue-50"
    >
      <SwatchIcon className="h-3.5 w-3.5" />
      参考技术标
    </button>
  ) : null;

  // 字数显示：仅三级及以下节点显示
  const wordCount = isDetailLevel ? (
    <span className="text-xs text-slate-500">
      {formatNumber(chapter.actual_word_count)}/{formatNumber(chapter.expected_word_count)}字
    </span>
  ) : null;

  // 操作按钮：生成正文 + 预览章节
  // 三级及以下节点：显示生成+预览；非三级节点仅预览
  // 生成按钮在忙时 disabled 但始终可见（不隐藏）
  // 文案根据是否已有内容自动切换
  const generateButton = isDetailLevel ? (
    <button
      type="button"
      disabled={!canGenerate}
      onClick={(e) => {
        e.stopPropagation();
        onStartWrite(chapter);
      }}
      title={!canGenerate ? '当前有任务在进行，请稍候' : undefined}
      className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs text-blue-600 hover:bg-blue-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400 disabled:hover:bg-slate-100"
    >
      <SparklesIcon className="h-3.5 w-3.5" />
      {chapter.has_content ? '重新生成' : '生成正文'}
    </button>
  ) : null;

  const previewButton = (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onPreview(chapter);
      }}
      className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-0.5 text-xs text-slate-600 hover:bg-slate-50"
    >
      <EyeIcon className="h-3.5 w-3.5" />
      预览章节
    </button>
  );

  const actionButton = (
    <div className="flex items-center gap-2">
      {generateButton}
      {previewButton}
    </div>
  );

  return (
    <div>
      <div
        className={`rounded-lg border transition ${
          isDetailLevel
            ? 'border-slate-200 bg-white hover:bg-slate-50'
            : 'border-slate-200 bg-slate-50 hover:bg-slate-100'
        }`}
        style={{ marginLeft: `${depth * 24}px` }}
      >
        <div className="flex items-center justify-between px-3 py-2">
          <div className="min-w-0 flex-1">
            <p
              className={`truncate ${
                isDetailLevel
                  ? 'text-sm font-medium text-slate-800'
                  : 'text-sm font-semibold text-slate-900'
              }`}
            >
              {chapter.title}
            </p>
            {/* 子条目预览（三级及以下节点，当有子项时显示） */}
            {hasSubItems && (
              <p className="mt-0.5 truncate text-xs text-slate-400">
                {chapter.children!.map((c) => c.title).join('  ')}
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-3">
            {refButton}
            {statusDot}
            {wordCount}
            {actionButton}
          </div>
        </div>
        {chapter.last_error_message && (
          <p className="px-3 pb-2 text-xs text-red-500">
            {chapter.last_error_message}
          </p>
        )}
      </div>
      {/* 递归渲染子节点（仅非三级节点展开） */}
      {children}
    </div>
  );
};

const DurableContentWorkspace: React.FC<DurableContentWorkspaceProps> = ({
  projectId,
  projectName,
}) => {
  const [dashboard, setDashboard] = useState<ContentDashboard | null>(null);
  const [exports, setExports] = useState<WordExportRecord[]>([]);
  const [references, setReferences] = useState<ReferenceBid[]>([]);
  const [contentTask, setContentTask] =
    useState<PersistedTaskSnapshot | null>(null);
  const [exportTask, setExportTask] =
    useState<PersistedTaskSnapshot | null>(null);
  const [selectedChapterId, setSelectedChapterId] = useState('');
  const [selectedReferences, setSelectedReferences] =
    useState<Record<string, ReferenceFileSelection>>({});
  const [referenceModalChapterId, setReferenceModalChapterId] = useState('');
  const [operation, setOperation] = useState<OperationDialog | null>(null);
  const [instruction, setInstruction] = useState('');
  const [referencePreview, setReferencePreview] =
    useState<ReferenceBidPreview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [actionKey, setActionKey] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [viewMode, setViewMode] = useState<ViewMode>('content');
  const [writingStyle, setWritingStyle] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [previewChapter, setPreviewChapter] = useState<ContentChapter | null>(null);
  const [localProjectName, setLocalProjectName] = useState(projectName || '');
  const [autoDownloadExportId, setAutoDownloadExportId] = useState<number | null>(null);

  const styleOptions = useMemo(() => {
    return dashboard?.available_styles && dashboard.available_styles.length > 0
      ? dashboard.available_styles
      : DEFAULT_STYLES;
  }, [dashboard]);

  const chapterTree = useMemo(() => {
    if (!dashboard?.chapters || dashboard.chapters.length === 0) return [];
    if (dashboard.chapter_tree && dashboard.chapter_tree.length > 0) {
      const hasChildren = dashboard.chapter_tree.some(
        (node) => node.children && node.children.length > 0
      );
      if (hasChildren) {
        return dashboard.chapter_tree;
      }
    }
    return buildChapterTree(dashboard.chapters);
  }, [dashboard]);

  const allLeafChapters = useMemo(() => {
    if (!dashboard?.chapters) return [];
    return getLeafChapters(dashboard.chapters);
  }, [dashboard]);

  const loadWorkspace = useCallback(async () => {
    if (!projectId) {
      setDashboard(null);
      setExports([]);
      setReferences([]);
      setIsLoading(false);
      return;
    }

    try {
      setError('');
      const [
        dashboardResponse,
        exportsResponse,
        referencesResponse,
      ] = await Promise.all([
        contentWorkspaceApi.get(projectId),
        wordExportApi.list(projectId),
        chapterReferenceApi.list(projectId).catch(() => null),
      ]);
      const nextDashboard = dashboardResponse.data.data;
      const nextExports = exportsResponse.data.data;
      setDashboard(nextDashboard);
      setWritingStyle(nextDashboard.writing_style || '');
      setContentTask(nextDashboard.latest_task);
      setExports(nextExports);
      setReferences(referencesResponse?.data.data || []);

      const defaultExpandedIds = new Set<string>();
      // 递归收集前三级节点 id（depth=0 一级, 1 二级, 2 三级 默认展开；depth>=3 四级+默认折叠）
      const collectDefaultExpanded = (nodes: ContentChapter[], depth: number) => {
        if (depth >= 3 || !nodes) return;
        nodes.forEach((node) => {
          defaultExpandedIds.add(node.outline_id);
          if (node.children && node.children.length > 0) {
            collectDefaultExpanded(node.children, depth + 1);
          }
        });
      };
      const rootTree =
        nextDashboard.chapter_tree && nextDashboard.chapter_tree.length > 0
          ? nextDashboard.chapter_tree
          : buildChapterTree(nextDashboard.chapters);
      collectDefaultExpanded(rootTree, 0);
      setExpandedIds(defaultExpandedIds);

      const currentExport = nextExports.find(
        (item) =>
          item.task_id
          && BLOCKING_TASK_STATUSES.has(item.status)
      );
      if (currentExport?.task_id) {
        const detailResponse = await wordExportApi.detail(
          projectId,
          currentExport.id
        );
        setExportTask(detailResponse.data.data.task);
      } else {
        setExportTask(null);
      }
    } catch (requestError) {
      setError(getErrorMessage(requestError, '正文工作台加载失败'));
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    setIsLoading(true);
    void loadWorkspace();
  }, [loadWorkspace]);

  useEffect(() => {
    setLocalProjectName(projectName || '');
  }, [projectName]);

  useEffect(() => {
    if (!dashboard?.chapters.length) {
      setSelectedChapterId('');
      return;
    }
    if (
      !dashboard.chapters.some(
        (chapter) => chapter.outline_id === selectedChapterId
      )
      && !chapterTree.some(
        (node) => findChapterInTree([node], selectedChapterId)
      )
    ) {
      const firstLeaf = allLeafChapters[0];
      const firstChapter = dashboard.chapters[0];
      setSelectedChapterId(
        firstLeaf?.outline_id ?? firstChapter?.outline_id ?? ''
      );
    }
  }, [dashboard, selectedChapterId, allLeafChapters, chapterTree]);

  const refreshAfterTask = useCallback(() => {
    const hasChapters = Boolean(dashboard?.chapters && dashboard.chapters.length > 0);
    const hasChildrenInTree = Boolean(
      dashboard?.chapter_tree
      && dashboard.chapter_tree.some(
        (node) => node.children && node.children.length > 0
      )
    );
    void loadWorkspace();
    if (hasChapters && !hasChildrenInTree) {
      window.setTimeout(() => {
        void loadWorkspace();
      }, 500);
    }
  }, [loadWorkspace, dashboard]);

  useTaskSubscription(
    contentTask,
    (snapshot) => {
      setContentTask(snapshot);
      setDashboard((current) =>
        current ? { ...current, latest_task: snapshot } : current
      );
    },
    refreshAfterTask
  );

  useTaskSubscription(
    exportTask,
    setExportTask,
    refreshAfterTask
  );

  // 监听导出列表变化，自动下载刚创建的导出
  useEffect(() => {
    if (!autoDownloadExportId) return;
    const target = exports.find((e) => e.id === autoDownloadExportId);
    if (!target) return;
    if (target.status === 30 && target.downloadable && projectId) {
      // 导出成功，自动下载
      void downloadExport(target);
      setAutoDownloadExportId(null);
      setActionKey('');
      setMessage('Word 文档导出完成，下载已开始！');
    } else if (target.status === 40 || target.status === 50) {
      // 导出失败
      setAutoDownloadExportId(null);
      setActionKey('');
      setError(`Word 导出失败：${target.error_message || '未知错误'}`);
    }
  }, [autoDownloadExportId, exports, projectId]);

  const selectedChapter = useMemo(() => {
    if (!dashboard || !selectedChapterId) return null;
    // Search in flat chapters first (leaf nodes with content)
    const foundInFlat = dashboard.chapters.find(
      (chapter) => chapter.outline_id === selectedChapterId
    );
    if (foundInFlat) return foundInFlat;
    // Search in tree (all nodes including non-leaf)
    if (dashboard.chapter_tree && dashboard.chapter_tree.length > 0) {
      const foundInTree = findChapterInTree(
        dashboard.chapter_tree,
        selectedChapterId
      );
      if (foundInTree) return foundInTree;
    }
    return null;
  }, [dashboard, selectedChapterId]);

  const contentTaskBlocks = Boolean(
    contentTask && BLOCKING_TASK_STATUSES.has(contentTask.status)
  );
  const exportTaskBlocks = Boolean(
    exportTask && BLOCKING_TASK_STATUSES.has(exportTask.status)
  );
  const anyTaskBlocks = contentTaskBlocks || exportTaskBlocks;

  const pendingChapters = allLeafChapters.filter(
    (chapter) => !chapter.has_content || Boolean(chapter.last_error_code)
  );
  // 两个独立目标：不再根据 pending 状态自动切换
  const regenerateAllTargets = allLeafChapters;
  const pendingOnlyTargets = pendingChapters;

  const makeReferenceSelection = (
    outlineId: string
  ): ChapterReferenceSelection | null => {
    const config = selectedReferences[outlineId];
    if (!config || config.fileIds.length === 0) return null;
    return {
      outline_id: outlineId,
      reference_file_ids: config.fileIds,
      reference_fragments: [],
      reference_weight: config.weight,
    };
  };

  const startBatchTask = async (targets?: typeof regenerateAllTargets) => {
    if (!projectId || !dashboard) return;
    const actualTargets = targets ?? regenerateAllTargets;
    if (actualTargets.length === 0) {
      setMessage('没有可生成的章节');
      return;
    }
    try {
      setActionKey('batch');
      setError('');
      setMessage('');
      const response = await contentWorkspaceApi.createBatchTask(
        projectId,
        {
          base_project_version: dashboard.project_version,
          base_outline_version: dashboard.outline_version,
          outline_ids: actualTargets.map(
            (chapter) => chapter.outline_id
          ),
          chapter_references: actualTargets
            .map((chapter) => makeReferenceSelection(chapter.outline_id))
            .filter(
              (item): item is ChapterReferenceSelection => item !== null
            ),
          idempotency_key: makeIdempotencyKey('content', projectId, dashboard.outline_version),
          writing_style: writingStyle || undefined,
        }
      );
      setContentTask(response.data.data);
      setMessage(response.data.msg);
    } catch (requestError) {
      setError(getErrorMessage(requestError, '正文任务创建失败'));
    } finally {
      setActionKey('');
    }
  };

  const startSingleTask = async () => {
    if (!projectId || !dashboard || !operation) return;
    try {
      setActionKey('single');
      setError('');
      setMessage('');
      const response = await contentWorkspaceApi.createSingleTask(
        projectId,
        operation.chapter.outline_id,
        operation.type,
        {
          base_project_version: dashboard.project_version,
          base_outline_version: dashboard.outline_version,
          base_content_version: operation.chapter.content_version_no,
          instruction: instruction.trim(),
          chapter_reference: makeReferenceSelection(
            operation.chapter.outline_id
          ),
          idempotency_key: makeIdempotencyKey(
            `content-${operation.type}`,
            projectId,
            dashboard.outline_version,
            operation.chapter.outline_id
          ),
          writing_style: writingStyle || undefined,
        }
      );
      setContentTask(response.data.data);
      setOperation(null);
      setInstruction('');
      setMessage(response.data.msg);
    } catch (requestError) {
      setError(getErrorMessage(requestError, '单章正文任务创建失败'));
    } finally {
      setActionKey('');
    }
  };

  const controlContentTask = async (
    action: 'pause' | 'resume' | 'cancel' | 'retry'
  ) => {
    if (!contentTask) return;
    if (
      action === 'cancel'
      && !window.confirm('确认取消当前正文任务？已完成章节会保留。')
    ) {
      return;
    }
    try {
      setActionKey(`task-${action}`);
      setError('');
      const request = {
        pause: taskApi.pause,
        resume: taskApi.resume,
        cancel: taskApi.cancel,
        retry: taskApi.retryFailedItems,
      }[action];
      const response = await request(contentTask.id);
      setContentTask(response.data.data);
      setMessage(response.data.msg);
      // 任务操作后刷新数据
      if (['cancel', 'retry'].includes(action)) {
        await refreshAfterTask();
      }
    } catch (requestError) {
      setError(getErrorMessage(requestError, '任务操作失败'));
    } finally {
      setActionKey('');
    }
  };

  const createExport = async () => {
    if (!projectId || !dashboard) return;
    try {
      setActionKey('export');
      setError('');
      setMessage('');
      const response = await wordExportApi.create(projectId, {
        base_project_version: dashboard.project_version,
        base_outline_version: dashboard.outline_version,
        base_content_version: dashboard.content_version,
        export_type: 'docx',
        idempotency_key: makeIdempotencyKey('word-export', projectId, dashboard.project_version, dashboard.outline_version, dashboard.content_version),
      });
      const newExport = response.data.data.export;
      const newTask = response.data.data.task;
      const newExportId = newExport.id;

      // 记录需要自动下载的 export id，导出完成后会自动触发
      setAutoDownloadExportId(newExportId);
      setExportTask(newTask);
      setExports((current) => [
        newExport,
        ...current.filter((item) => item.id !== newExportId),
      ]);
      setMessage('Word 导出任务已创建，请稍候...');
      // 导出任务创建后刷新数据
      await refreshAfterTask();
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'Word 导出任务创建失败'));
      setActionKey('');
    }
  };

  const cancelExport = async () => {
    if (!exportTask) return;
    if (!window.confirm('确认取消当前 Word 导出任务？')) return;
    try {
      setActionKey('export-cancel');
      setError('');
      const response = await taskApi.cancel(exportTask.id);
      setExportTask(response.data.data);
      setAutoDownloadExportId(null);
      setMessage(response.data.msg);
    } catch (requestError) {
      setError(getErrorMessage(requestError, '导出任务取消失败'));
    } finally {
      setActionKey('');
    }
  };

  const downloadExport = async (record: WordExportRecord) => {
    if (!projectId) return;
    try {
      setActionKey(`download-${record.id}`);
      setError('');
      const response = await wordExportApi.download(projectId, record.id);
      saveAs(
        response.data,
        record.file_name || `${projectName || '标书文档'}.docx`
      );
      setMessage('Word 文档下载已开始');
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'Word 文档下载失败'));
    } finally {
      setActionKey('');
    }
  };

  const previewReference = async (fileId: number) => {
    if (!projectId || !fileId) return;
    try {
      setActionKey('reference-preview');
      setError('');
      const response = await chapterReferenceApi.preview(
        projectId,
        fileId
      );
      setReferencePreview(response.data.data);
    } catch (requestError) {
      setError(getErrorMessage(requestError, '参考技术标预览失败'));
    } finally {
      setActionKey('');
    }
  };

  const handleToggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectChapter = (chapter: ContentChapter) => {
    setSelectedChapterId(chapter.outline_id);
  };

  const handleToggleImageRich = async (chapter: ContentChapter) => {
    if (!projectId) return;
    try {
      setActionKey(`image-rich-${chapter.outline_id}`);
      setError('');
      const newValue = !chapter.is_image_rich;
      await contentWorkspaceApi.updateChapterConfig(
        projectId,
        chapter.outline_id,
        { is_image_rich: newValue }
      );
      setDashboard((current) => {
        if (!current) return current;
        const updateChapter = (chapters: ContentChapter[]): ContentChapter[] =>
          chapters.map((ch) => {
            if (ch.outline_id === chapter.outline_id) {
              return { ...ch, is_image_rich: newValue };
            }
            if (ch.children) {
              return { ...ch, children: updateChapter(ch.children) };
            }
            return ch;
          });
        return {
          ...current,
          chapters: updateChapter(current.chapters),
          chapter_tree: current.chapter_tree
            ? updateChapter(current.chapter_tree)
            : undefined,
        };
      });
    } catch (requestError) {
      setError(getErrorMessage(requestError, '更新图文并茂设置失败'));
    } finally {
      setActionKey('');
    }
  };

  type ChapterConfigField = 'expected_word_count' | 'is_image_rich';

const handleChapterSettings = async (
  chapter: ContentChapter,
  field: ChapterConfigField,
  value: number | boolean
) => {
  if (!projectId) return;
  try {
    setActionKey(`chapter-config-${chapter.outline_id}`);
    setError('');
    const data: { is_image_rich?: boolean; expected_word_count?: number } = {
      [field]: value,
    };
    await contentWorkspaceApi.updateChapterConfig(
      projectId,
      chapter.outline_id,
      data
    );
    setDashboard((current) => {
      if (!current) return current;
      const updateChapter = (chapters: ContentChapter[]): ContentChapter[] =>
        chapters.map((ch) => {
          if (ch.outline_id === chapter.outline_id) {
            return { ...ch, [field]: value } as ContentChapter;
          }
          if (ch.children) {
            return { ...ch, children: updateChapter(ch.children) };
          }
          return ch;
        });
      return {
        ...current,
        chapters: updateChapter(current.chapters),
        chapter_tree: current.chapter_tree
          ? updateChapter(current.chapter_tree)
          : undefined,
      };
    });
  } catch (requestError) {
    setError(getErrorMessage(requestError, '更新章节设置失败'));
  } finally {
    setActionKey('');
  }
};

  const handleUpdateStyle = async (style: string) => {
    if (!projectId) return;
    try {
      setWritingStyle(style);
      setActionKey('style-update');
      setError('');
      await contentWorkspaceApi.updateStyle(projectId, style);
      setMessage('正文风格已更新');
    } catch (requestError) {
      setError(getErrorMessage(requestError, '更新正文风格失败'));
      setWritingStyle(dashboard?.writing_style || '');
    } finally {
      setActionKey('');
    }
  };

  const handleOpenPreview = (chapter: ContentChapter) => {
    setPreviewChapter(chapter);
  };

  const handleClosePreview = () => {
    setPreviewChapter(null);
  };

  const handleStartWriteFromTree = (chapter: ContentChapter) => {
    setSelectedChapterId(chapter.outline_id);
    setViewMode('content');
    setOperation({ chapter, type: 'regenerate' });
    setInstruction('');
  };

  const handleOpenReference = (chapter: ContentChapter) => {
    setReferenceModalChapterId(chapter.outline_id);
  };

  /** 递归渲染目录模式的章节树，三级及以下不再展开子节点 */
  const renderOutlineTree = (nodes: ContentChapter[], depth: number) => {
    return nodes.map((chapter) => {
      // 三级及以下节点不再递归展开，子项以预览文本形式在行内显示
      const shouldRecurse = depth < 2;
      const childrenTree = shouldRecurse && chapter.children && chapter.children.length > 0
        ? renderOutlineTree(chapter.children, depth + 1)
        : null;
      return (
        <OutlineChapterRow
          key={chapter.outline_id}
          chapter={chapter}
          depth={depth}
          onPreview={handleOpenPreview}
          onStartWrite={(ch) => {
            setSelectedChapterId(ch.outline_id);
            setViewMode('content');
            setOperation({ chapter: ch, type: 'regenerate' });
            setInstruction('');
          }}
          onOpenReference={handleOpenReference}
          selectedReferences={selectedReferences}
          canGenerate={canGenerate}
        >
          {childrenTree}
        </OutlineChapterRow>
      );
    });
  };

  if (!projectId) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white px-6 py-14 text-center">
        <DocumentTextIcon className="mx-auto h-11 w-11 text-slate-300" />
        <h3 className="mt-4 font-medium text-slate-800">请先保存项目</h3>
        <p className="mt-2 text-sm text-slate-500">
          项目保存后才能读取正文版本和导出记录。
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white px-6 py-16 text-center text-sm text-slate-500">
        <ArrowPathIcon className="mx-auto mb-3 h-6 w-6 animate-spin" />
        正在加载正文工作台...
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 px-6 py-10 text-center">
        <ExclamationTriangleIcon className="mx-auto h-9 w-9 text-red-500" />
        <p className="mt-3 text-sm text-red-700">
          {error || '正文工作台暂不可用'}
        </p>
        <button
          type="button"
          onClick={() => void loadWorkspace()}
          className="mt-5 rounded-xl bg-red-600 px-4 py-2 text-sm text-white"
        >
          重新加载
        </button>
      </div>
    );
  }

  const exportDisabledReasons: string[] = [];
  if (dashboard.flow_status < 30) {
    exportDisabledReasons.push('请先确认项目目录');
  }
  if (dashboard.summary.total_chapters === 0) {
    exportDisabledReasons.push('暂无可导出的叶子章节');
  }
  if (dashboard.summary.completed_chapters === 0) {
    exportDisabledReasons.push('请先生成至少一个章节的正文');
  }
  if (anyTaskBlocks) {
    exportDisabledReasons.push('请等待当前任务结束');
  }
  const canExport = exportDisabledReasons.length === 0;
  const canGenerate = (
    dashboard.capabilities.generation_enabled
    && allLeafChapters.length > 0
    && !anyTaskBlocks
  );

  const renderChapterTree = (nodes: ContentChapter[], level: number) => {
    return nodes.map((chapter) => (
      <div key={chapter.outline_id}>
        <ChapterNode
          chapter={chapter}
          level={level}
          expandedIds={expandedIds}
          onToggle={handleToggleExpand}
          onSelect={handleSelectChapter}
          onToggleImageRich={handleToggleImageRich}
          onStartWrite={handleStartWriteFromTree}
          selectedId={selectedChapterId}
          canGenerate={canGenerate}
        />
      </div>
    ));
  };

  return (
    <div className="flex h-screen flex-col bg-white">
      {/* Top toolbar */}
      <header className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <div className="flex items-center gap-4">
          <input
            type="text"
            value={localProjectName}
            onChange={(e) => setLocalProjectName(e.target.value)}
            placeholder="请输入文档标题"
            className="w-64 rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:border-blue-400 focus:outline-none"
          />
          <button
            type="button"
            onClick={() => void createExport()}
            disabled={!canExport || Boolean(exportTask) || Boolean(actionKey === 'export')}
            title={!canExport ? `无法导出：${exportDisabledReasons.join('；')}` : undefined}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {Boolean(exportTask) || actionKey === 'export' ? (
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
            ) : (
              <ArrowDownTrayIcon className="h-4 w-4" />
            )}
            {exportTask
              ? `导出中 ${exportTask.progress_percent}%`
              : '下载标书'}
          </button>
        </div>

        <div className="flex items-center gap-4">
          {/* View mode toggle */}
          <div className="flex rounded-lg bg-slate-100 p-0.5">
            <button
              type="button"
              onClick={() => setViewMode('outline')}
              className={`rounded-md px-3 py-1.5 text-sm transition ${
                viewMode === 'outline'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              目录模式
            </button>
            <button
              type="button"
              onClick={() => setViewMode('content')}
              className={`rounded-md px-3 py-1.5 text-sm transition ${
                viewMode === 'content'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              正文模式
            </button>
          </div>

          {/* Writing style selector */}
          <div className="flex items-center gap-2">
            <SwatchIcon className="h-4 w-4 text-slate-400" />
            <span className="text-sm text-slate-500">正文风格</span>
            <select
              value={writingStyle || '通用'}
              onChange={(e) => void handleUpdateStyle(e.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm focus:border-blue-400 focus:outline-none"
            >
              {styleOptions.map((style) => (
                <option key={style.key} value={style.key}>
                  {style.label}
                </option>
              ))}
            </select>
          </div>

          {/* Progress indicator */}
          <div className="text-sm text-slate-500">
            总计章节：{dashboard.summary.total_chapters} | 已完成：{dashboard.summary.completed_chapters} | 实际字数: {formatNumber(dashboard.summary.actual_word_count)}
          </div>

          {/* Progress bar */}
          <div className="w-48">
            <div className="h-2 overflow-hidden rounded-full bg-slate-100">
              <div
                className={`h-full rounded-full transition-all ${
                  contentTask && BLOCKING_TASK_STATUSES.has(contentTask.status)
                    ? 'bg-amber-500'
                    : 'bg-blue-500'
                }`}
                style={{ width: `${dashboard.summary.progress_percent}%` }}
              />
            </div>
            <p className="mt-0.5 text-right text-xs text-slate-400">
              {contentTask && BLOCKING_TASK_STATUSES.has(contentTask.status)
                ? `后台生成中 ${contentTask.progress_percent}%`
                : `${dashboard.summary.progress_percent}%`}
            </p>
          </div>

          {/* Batch generate buttons */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void startBatchTask()}
              disabled={!canGenerate || Boolean(actionKey)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--bid-color-primary)] px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
            >
              <SparklesIcon className="h-4 w-4" />
              重新生成全部
            </button>
            {pendingChapters.length > 0 && (
              <button
                type="button"
                onClick={() => void startBatchTask(pendingOnlyTargets)}
                disabled={!canGenerate || Boolean(actionKey)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-40"
                title={`仅生成未完成/失败的 ${pendingChapters.length} 个章节`}
              >
                <SparklesIcon className="h-3.5 w-3.5" />
                生成未完成({pendingChapters.length})
              </button>
            )}
          </div>

          {/* Refresh button */}
          <button
            type="button"
            onClick={() => void loadWorkspace()}
            className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50"
            title="刷新状态"
          >
            <ArrowPathIcon className="h-4 w-4" />
          </button>
        </div>
      </header>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left sidebar - Chapter tree */}
        <aside className="flex w-80 flex-col border-r border-slate-100">
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-700">章节目录</h3>
            <span className="text-xs text-slate-400">
              {dashboard.summary.progress_percent}%
            </span>
          </div>

          {contentTask && BLOCKING_TASK_STATUSES.has(contentTask.status) && (
            <div className="border-b border-slate-100 bg-blue-50 px-3 py-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-blue-700">
                  {contentTask.status_name}
                </span>
                <span className="text-xs text-blue-600">
                  {contentTask.progress_percent}%
                </span>
              </div>
              <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-blue-200">
                <div
                  className="h-full rounded-full bg-blue-500 transition-all"
                  style={{ width: `${contentTask.progress_percent}%` }}
                />
              </div>
              <div className="mt-1.5 flex gap-1">
                {[10, 20].includes(contentTask.status) && (
                  <button
                    type="button"
                    onClick={() => void controlContentTask('pause')}
                    className="rounded bg-white px-2 py-0.5 text-xs text-blue-600 hover:bg-blue-50"
                  >
                    暂停
                  </button>
                )}
                {contentTask.status === 90 && (
                  <button
                    type="button"
                    onClick={() => void controlContentTask('resume')}
                    className="rounded bg-white px-2 py-0.5 text-xs text-blue-600 hover:bg-blue-50"
                  >
                    恢复
                  </button>
                )}
                {contentTask.failed_units > 0 && (
                  <button
                    type="button"
                    onClick={() => void controlContentTask('retry')}
                    className="rounded bg-white px-2 py-0.5 text-xs text-blue-600 hover:bg-blue-50"
                  >
                    重试
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => void controlContentTask('cancel')}
                  className="rounded bg-white px-2 py-0.5 text-xs text-red-500 hover:bg-red-50"
                >
                  取消
                </button>
              </div>
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-2">
            {chapterTree.length === 0 ? (
              <div className="rounded-lg bg-slate-50 p-8 text-center text-sm text-slate-500">
                请先确认标书目录
              </div>
            ) : (
              renderChapterTree(chapterTree, 0)
            )}
          </div>
        </aside>

        {/* Right main area */}
        <main className="flex flex-1 flex-col overflow-hidden">
          {/* 目录模式：显示三级树形结构 */}
          {viewMode === 'outline' ? (
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-700">
                  章节目录（共 {chapterTree.length} 个顶级章节）
                </h3>
                <div className="flex items-center gap-4 text-xs text-slate-500">
                  <span>
                    总计: {dashboard?.summary.total_chapters || 0} 节
                  </span>
                  <span>
                    已完成: {dashboard?.summary.completed_chapters || 0}
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                {renderOutlineTree(chapterTree, 0)}
              </div>
            </div>
          ) : selectedChapter ? (
            selectedChapter.children && selectedChapter.children.length > 0 ? (
              // Non-leaf node: show introduction (if any) + children list
              <>
                {/* Chapter header */}
                <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
                  <div className="flex items-center gap-3">
                    {selectedChapter.parent_titles.length > 0 && (
                      <span className="text-xs text-slate-400">
                        {selectedChapter.parent_titles.join(' / ')}
                      </span>
                    )}
                    <h2 className="text-lg font-semibold text-slate-800">
                      {selectedChapter.title}
                    </h2>
                    <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                      第 {selectedChapter.level} 级 · 共 {selectedChapter.children.length} 个子章节
                    </span>
                    {selectedChapter.has_content && (
                      <span className="rounded bg-emerald-50 px-2 py-0.5 text-xs text-emerald-600">
                        导语 {formatNumber(selectedChapter.actual_word_count)} 字
                      </span>
                    )}
                  </div>
                </div>

                {/* Introduction content (if exists) */}
                {selectedChapter.has_content && (
                  <div className="border-b border-slate-100 bg-amber-50/40 px-6 py-4">
                    <div className="mb-1 flex items-center gap-2 text-xs text-amber-700">
                      <span className="font-medium">章节导语</span>
                    </div>
                    <div
                      className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700"
                      style={{ fontSize: '14px' }}
                    >
                      {selectedChapter.content}
                    </div>
                  </div>
                )}

                {/* Children summary */}
                <div className="flex items-center gap-4 border-b border-slate-100 bg-slate-50 px-6 py-3 text-sm">
                  {(() => {
                    const children = selectedChapter.children;
                    const completed = children.filter(
                      (c) => c.generation_status === 30
                    ).length;
                    const failed = children.filter(
                      (c) => c.generation_status === 50
                    ).length;
                    const pending = children.filter(
                      (c) => !c.generation_status || [0, 10, 20].includes(c.generation_status)
                    ).length;
                    const totalWords = children.reduce(
                      (sum, c) => sum + (c.actual_word_count || 0),
                      0
                    );
                    return (
                      <>
                        <span className="text-slate-500">
                          子章节：{children.length}
                        </span>
                        <span className="text-emerald-600">
                          已完成：{completed}
                        </span>
                        {pending > 0 && (
                          <span className="text-blue-600">
                            进行中：{pending}
                          </span>
                        )}
                        {failed > 0 && (
                          <span className="text-red-600">
                            失败：{failed}
                          </span>
                        )}
                        <span className="text-slate-500">
                          总字数：{formatNumber(totalWords)}
                        </span>
                      </>
                    );
                  })()}
                </div>

                {/* Children list */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  <div className="mb-3 text-sm font-medium text-slate-600">
                    子章节列表（点击可进入下一级）
                  </div>
                  <div className="space-y-2">
                    {selectedChapter.children.map((child) => (
                      <button
                        key={child.outline_id}
                        type="button"
                        onClick={() =>
                          setSelectedChapterId(child.outline_id)
                        }
                        className={`flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left transition hover:bg-slate-50 ${
                          selectedChapterId === child.outline_id
                            ? 'border-blue-200 bg-blue-50'
                            : 'border-slate-200'
                        }`}
                      >
                        {(() => {
                          const statusMap: Record<number, { label: string; color: string }> = {
                            0: { label: '待处理', color: 'text-slate-400' },
                            10: { label: '排队中', color: 'text-slate-400' },
                            20: { label: '执行中', color: 'text-blue-500' },
                            30: { label: '已完成', color: 'text-emerald-500' },
                            40: { label: '部分失败', color: 'text-amber-500' },
                            50: { label: '失败', color: 'text-red-500' },
                          };
                          const statusInfo = statusMap[child.generation_status] || {
                            label: '未知',
                            color: 'text-slate-400',
                          };
                          const hasChildren = child.children && child.children.length > 0;
                          return (
                            <>
                              <ChevronRightIcon className="h-4 w-4 shrink-0 text-slate-300" />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="truncate text-sm font-medium text-slate-800">
                                    {child.title}
                                  </span>
                                  {hasChildren && (
                                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">
                                      {child.children!.length} 子项
                                    </span>
                                  )}
                                </div>
                                <div className="mt-0.5 flex items-center gap-3 text-xs text-slate-500">
                                  <span className={statusInfo.color}>
                                    {statusInfo.label}
                                  </span>
                                  <span>
                                    {formatNumber(child.actual_word_count)} 字
                                  </span>
                                  {child.expected_word_count > 0 && (
                                    <span className="text-slate-400">
                                      目标 {formatNumber(child.expected_word_count)} 字
                                    </span>
                                  )}
                                </div>
                              </div>
                            </>
                          );
                        })()}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            ) : (
            <>
              {/* Chapter header */}
              <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
                <div className="flex items-center gap-3">
                  {selectedChapter.parent_titles.length > 0 && (
                    <span className="text-xs text-slate-400">
                      {selectedChapter.parent_titles.join(' / ')}
                    </span>
                  )}
                  <h2 className="text-lg font-semibold text-slate-800">
                    {selectedChapter.title}
                  </h2>
                  <span className="text-xs text-slate-500">
                    实际 {formatNumber(selectedChapter.actual_word_count)} 字
                    {selectedChapter.expected_word_count > 0
                      ? ` · 目标 ${formatNumber(selectedChapter.expected_word_count)} 字`
                      : ''}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleOpenPreview(selectedChapter)}
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50"
                  >
                    <EyeIcon className="h-4 w-4" />
                    预览章节
                  </button>
                  {(['regenerate', 'expand', 'rewrite'] as SingleOperation[]).map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => {
                        setOperation({ chapter: selectedChapter, type });
                        setInstruction('');
                      }}
                      disabled={
                        !canGenerate
                        || (type !== 'regenerate' && !selectedChapter.has_content)
                      }
                      className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                    >
                      {operationLabels[type]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Settings row */}
              <div className="flex items-center gap-6 border-b border-slate-100 bg-slate-50 px-6 py-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">参考技术标</span>
                  <button
                    type="button"
                    onClick={() => setReferenceModalChapterId(selectedChapter.outline_id)}
                    className="rounded-lg border border-blue-200 bg-white px-3 py-1 text-sm text-blue-600 hover:bg-blue-50"
                  >
                    配置参考
                  </button>
                  {(() => {
                    const cfg = selectedReferences[selectedChapter.outline_id];
                    if (!cfg || cfg.fileIds.length === 0) {
                      return (
                        <span className="text-xs text-slate-400">
                          未配置
                        </span>
                      );
                    }
                    const weightLabel = { low: '低', medium: '中', high: '高' }[cfg.weight];
                    const names = cfg.fileIds
                      .map((id) => references.find((r) => r.id === id)?.file_name || `#${id}`)
                      .filter(Boolean);
                    const displayNames = names.slice(0, 2).join('、');
                    const extra = names.length > 2 ? ` +${names.length - 2}` : '';
                    return (
                      <span className="text-xs text-slate-500" title={names.join('\n')}>
                        已选 {cfg.fileIds.length} 个 · 参考{weightLabel} · {displayNames}{extra}
                      </span>
                    );
                  })()}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">目标字数</span>
                  <input
                    type="number"
                    value={selectedChapter.expected_word_count}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      if (val > 0) {
                        void handleChapterSettings(
                          selectedChapter,
                          'expected_word_count',
                          val
                        );
                      }
                    }}
                    className="w-24 rounded-lg border border-slate-200 px-2 py-1 text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">图文并茂</span>
                  <button
                    type="button"
                    onClick={() => void handleToggleImageRich(selectedChapter)}
                    className={`relative inline-flex h-5 w-10 items-center rounded-full transition ${
                      selectedChapter.is_image_rich ? 'bg-blue-500' : 'bg-slate-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                        selectedChapter.is_image_rich ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Content area */}
              <div className="flex-1 overflow-y-auto px-6 py-4">
                {selectedChapter.last_error_message && (
                  <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                    {selectedChapter.last_error_message}
                  </div>
                )}

                {selectedChapter.has_content ? (
                  <article className="prose prose-slate max-w-none">
                    <div
                      className="whitespace-pre-wrap text-slate-700 leading-relaxed"
                      style={{ fontSize: '14px' }}
                    >
                      {selectedChapter.content}
                    </div>
                  </article>
                ) : (
                  <div className="flex flex-col items-center justify-center py-20">
                    <DocumentTextIcon className="h-16 w-16 text-slate-200" />
                    <p className="mt-4 text-slate-400">本章尚未生成正文</p>
                    <button
                      type="button"
                      onClick={() => {
                        setOperation({ chapter: selectedChapter, type: 'regenerate' });
                        setInstruction('');
                      }}
                      disabled={!canGenerate}
                      className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[var(--bid-color-primary)] px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
                    >
                      <SparklesIcon className="h-4 w-4" />
                      开始编写
                    </button>
                  </div>
                )}
              </div>
            </>
          ) ) : (
            <div className="flex flex-1 flex-col items-center justify-center text-slate-400">
              <DocumentTextIcon className="h-16 w-16" />
              <p className="mt-4 text-sm">请选择需要查看的章节</p>
            </div>
          )}
        </main>
      </div>

      {/* Status bar */}
      {(error || message) && (
        <div className="border-t border-slate-100 px-4 py-2">
          {error && (
            <div className="whitespace-pre-wrap rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
          {message && (
            <div className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              {message}
            </div>
          )}
        </div>
      )}

      {/* Export history */}
      {(exports.length > 0 || exportTask) && (
        <div className="border-t border-slate-100 px-4 py-2 bg-slate-50">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-slate-600">导出历史</span>
            {exportTask && (
              <button
                type="button"
                onClick={() => void cancelExport()}
                className="text-xs text-red-500 hover:text-red-600"
              >
                取消导出任务
              </button>
            )}
          </div>
          <div className="flex gap-2 overflow-x-auto">
            {exportTask && (
              <div className="flex shrink-0 items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs">
                <span className="text-blue-600">{exportTask.status_name}</span>
                <span className="text-slate-500">{exportTask.progress_percent}%</span>
              </div>
            )}
            {exports.map((record) => (
              <div
                key={record.id}
                className="flex shrink-0 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs"
              >
                <span className="text-slate-600">{record.file_name}</span>
                <span className={`text-${record.status === 30 ? 'emerald' : 'slate'}-500`}>
                  {record.status_name}
                </span>
                {record.downloadable && (
                  <button
                    type="button"
                    onClick={() => void downloadExport(record)}
                    className="text-blue-500 hover:text-blue-600"
                  >
                    下载
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Operation dialog */}
      {operation && (
        <div className="fixed inset-0 z-[80] grid place-items-center bg-slate-950/40 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  {operationLabels[operation.type]}正文
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  {operation.chapter.title}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOperation(null)}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <label
              htmlFor="content-instruction"
              className="mt-5 block text-sm font-medium text-slate-700"
            >
              补充要求
            </label>
            <textarea
              id="content-instruction"
              value={instruction}
              onChange={(event) => setInstruction(event.target.value)}
              maxLength={2000}
              rows={5}
              placeholder="可选，例如：增加实施步骤和验收指标"
              className="mt-2 w-full rounded-xl border-slate-200 text-sm"
            />
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setOperation(null)}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm text-slate-600"
              >
                取消
              </button>
              <button
                type="button"
                onClick={() => void startSingleTask()}
                disabled={actionKey === 'single'}
                className="rounded-xl bg-[var(--bid-color-primary)] px-5 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                创建{operationLabels[operation.type]}任务
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview dialog */}
      {previewChapter && (
        <div className="fixed inset-0 z-[80] grid place-items-center bg-slate-950/40 p-4">
          <div className="max-h-[85vh] w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
              <div>
                <h3 className="font-semibold text-slate-900">
                  {previewChapter.title}
                </h3>
                <p className="mt-1 text-xs text-slate-400">
                  {formatNumber(previewChapter.actual_word_count)} 字 · V
                  {previewChapter.content_version_no}
                </p>
              </div>
              <button
                type="button"
                onClick={handleClosePreview}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="max-h-[65vh] overflow-y-auto px-6 py-5">
              {previewChapter.has_content ? (
                <div className="whitespace-pre-wrap text-slate-700 leading-relaxed">
                  {previewChapter.content}
                </div>
              ) : (
                <div className="py-16 text-center text-slate-400">
                  本章尚未生成正文
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reference preview dialog */}
      {referencePreview && (
        <div className="fixed inset-0 z-[80] grid place-items-center bg-slate-950/40 p-4">
          <div className="max-h-[85vh] w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5">
              <div>
                <h3 className="font-semibold text-slate-900">
                  {referencePreview.file_name}
                </h3>
                <p className="mt-1 text-xs text-slate-400">
                  {referencePreview.project_name} · 已预览
                  {' '}{referencePreview.end_offset}/
                  {referencePreview.total_characters} 字符
                </p>
              </div>
              <button
                type="button"
                onClick={() => setReferencePreview(null)}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="max-h-[65vh] overflow-y-auto whitespace-pre-wrap px-6 py-5 text-sm leading-7 text-slate-700">
              {referencePreview.content}
            </div>
          </div>
        </div>
      )}

      {/* Reference bid config modal */}
      {referenceModalChapterId && (
        <ReferenceBidConfigModal
          isOpen={!!referenceModalChapterId}
          projectId={projectId as number}
          chapterTitle={
            findChapterById(chapterTree, referenceModalChapterId)?.title
            || selectedChapter?.title
            || ''
          }
          availableReferences={references}
          initialSelection={
            selectedReferences[referenceModalChapterId] || {
              fileIds: [],
              weight: 'medium',
            }
          }
          onClose={() => setReferenceModalChapterId('')}
          onConfirm={(selection) => {
            setSelectedReferences((current) => ({
              ...current,
              [referenceModalChapterId]: selection,
            }));
            setReferenceModalChapterId('');
          }}
        />
      )}
    </div>
  );
};

export default DurableContentWorkspace;